import { filter, uniqBy } from 'lodash-es';
import moment from 'moment';
import * as DateTool from './DateToolService';
import {convertStringDateToDate} from "./DateToolService";

/**
 * Validate the new allocations to see if any of them overlaps already existing allocation. In the case of overlaps
 * then return a list of periods that is being overlapped.
 *
 * @param newPeriods
 * @param oldPeriods
 * @returns {*}
 */
export const validateAddAllocationPeriod = (newPeriods, oldPeriods) => {
    let newDates = [];
    let oldDates = [];

    // Generate calendars for the old and the new periods.
    filter(newPeriods, (period) => {
        newDates = [...newDates, ...DateTool.generateValidatorCalendar(period.from, period.to)];
    });
    filter(oldPeriods, (period) => {
        // Ensure that the calendar is not validated by checking for the form and to period.
        if (period.from && period.to) {
            oldDates = [...oldDates, ...DateTool.generateValidatorCalendar(period.from, period.to)];
        }
    });
    // Remove duplicates, which is only needed on the new periods as the old periods are already unique
    newDates = uniqBy(newDates);
    return DateTool.checkForOverlap(newDates, oldDates);
};

/**
 * Validates the add allocation inputs.
 *
 * @param allocations
 */
export const validateAddAllocationInputs = (allocations) => {
    let validator = {
        valid: true,
        messages: []
    };
    allocations.forEach((allocation) => {
        if (!validateDates(allocation, validator)
            || !validateAllocationType(allocation, validator)
            || !validateReleaseDate(allocation, validator)
        ) {
            validator.valid = false;
            validator.message = "Something went wrong";
        }
    });

    return validator;
};

/**
 * Validate if the update has deduction of units or a worse release rule.
 *
 * @param original
 * @param allocation
 * @param trusted
 * @returns {boolean|*}
 */
export const validateUpdateAllocationPossible = (original, allocation, trusted) => {
    return trusted || (!isDeductionOrWorseRelease(original, allocation) && !allocationWorseTypeChanged(original, allocation));
};

/**
 * Check if the allocation type has changed.
 *
 * @param original
 * @param allocation
 * @returns {boolean}
 */
export const allocationWorseTypeChanged = (original, allocation) => {
    // Everyone can change type to allotment or free sale as its better than request or blocked
    if (allocation.type === 'FREESALE' || allocation.type === 'ALLOTMENT') {
        return false;
    }
    return original.type !== allocation.type;
};

/**
 * Check if is a deduction of units or worsening release date/days.
 *
 * @param original
 * @param allocation
 * @returns {boolean|*}
 */
export const isDeductionOrWorseRelease = (original, allocation) => {
    // If free sale then dont evaluate unit difference
    let deducting = allocation.type === 'FREESALE' ? false : allocation.units < original.units;
    let worseRelease;
    if(original.releaseMethod === 'days') {
        worseRelease = allocation.releaseDays > original.releaseDays
    } else {
        worseRelease = convertStringDateToDate(original.releaseDate).diff(convertStringDateToDate(allocation.releaseDate)) > 0;
    }
    return deducting || worseRelease;
};

/*
 * ============================
 * VUELIDATOR CUSTOM VALIDATORS
 * ============================
 */

/**
 * Validate that the date is correct type
 *
 * @returns {boolean}
 * @param date
 */
export const validateValidDate = (date) => {
    // Only validate if the whole date is stated
    if (date && date.length === 10) {
        return moment(date, 'DD/MM/YYYY')._isValid;
    }
    return false;
};

/**
 * Validate that the release date is before or equal the from date for period.
 *
 * @param date
 * @param allocation
 * @returns {boolean}
 */
export const beforeOrEqualFrom = (date, allocation) => {
    const fromDate = moment(allocation.from, 'DD/MM/YYYY');
    const dateDate = moment(date, 'DD/MM/YYYY');
    return fromDate.diff(dateDate) >= 0;
};

/**
 * Validate that the release date is before or equal the from date for period.
 *
 * @param date
 * @param allocation
 * @returns {boolean}
 */
export const beforeOrEqualTo = (date, allocation) => {
    const toDate = moment(allocation.to, 'DD/MM/YYYY');
    const dateDate = moment(date, 'DD/MM/YYYY');
    return toDate.diff(dateDate) > 0;
};

/**
 * Validates that the date is within the year chosen.
 *
 * @param date
 * @param chosenYear
 */
export const validateWithinYear = (date, chosenYear) => {
    // Only validate if the whole date is stated
    if (date && date.length === 10) {
        const startOfYear = moment('01/01/' + chosenYear, 'DD/MM/YYYY');
        const endOfYear = moment('31/12/' + chosenYear, 'DD/MM/YYYY');
        const momentDate = moment(date, 'DD/MM/YYYY');
        return momentDate.diff(startOfYear) >= 0 && momentDate.diff(endOfYear) <= 0;
    }
    return false;
};

/**
 * Validate that the to date is after or equal the from date.
 *
 * @param date
 * @param row
 * @returns {boolean}
 */
export const validateDateAfter = (date, row) => {
    // Only validate if the whole date is stated
    if (date && row && date.length === 10) {
        const from = moment(row.from, 'DD/MM/YYYY');
        return moment(date, 'DD/MM/YYYY').diff(from) >= 0;
    }
    return false;
};

/**
 * Validates if the to and from dates are set on add allocation.
 *
 * @param allocation
 * @param validator
 * @returns mixed
 */
const validateDates = (allocation, validator) => {
    if (!allocation.from || !allocation.to) {
        validator.valid = false;
        validator.messages.push("To and from date has to be set");
    } else if (!moment(allocation.from, 'DD/MM/YYYY')._isValid || !moment(allocation.to, 'DD/MM/YYYY')._isValid) {
        validator.valid = false;
        validator.messages.push("To or from date is invalid, should be formatted DD/MM/YYYY");
    } else if (allocation.from.length !== 10 || allocation.to.length !== 10) {
        validator.valid = false;
        validator.messages.push("To and from date has to be formatted DD/MM/YYYY");
    } else if (moment(allocation.from).diff(moment(allocation.to)) > 0) {
        validator.valid = false;
        validator.messages.push("To date has to be after the from date");
    }
    return validator;
};

/**
 * Validate release date.
 *
 * @param allocation
 * @param validator
 * @returns {*}
 */
const validateReleaseDate = (allocation, validator) => {
    if (!allocation.releaseDays && allocation.releaseDate) {
        if(allocation.releaseDate.length !== 10) {
            validator.valid = false;
            validator.messages.push('Release date has to be valid in the format DD/MM/YYYY')
        }
        if(moment(allocation.releaseDate).diff(moment(allocation.to)) >= 0) {
            validator.valid = false;
            validator.messages.push('Release date has to be before or equal to the from date')
        }
    } else if (allocation.releaseDays && !allocation.releaseDate) {
        if (allocation.releaseDays <= 0) {
            validator.valid = false;
            validator.messages.push('Release days has to be 1 or more days')
        }
    }
    return validator;
};

/**
 * Validates allotment type rules.
 *
 * @param allocation
 * @param validator
 * @returns mixed
 */
const validateAllocationType = (allocation, validator) => {
    if (!allocation.type || allocation.type.length === 0) {
        validator.valid = false;
        validator.messages.push("Allocation type has to be set");
    } else if (allocation.type === 'ALLOTMENT') {
        if (!allocation.units || (parseInt(allocation.units) <= 0 || parseInt(allocation.units) > 10)) {
            validator.valid = false;
            validator.messages.push("Units has to be set to a value between 1 and 10");
        }
        if (allocation.releaseType.length === 0 || (!allocation.releaseDate && !allocation.releaseDays)) {
            validator.valid = false;
            validator.messages.push("Release days/date has to be set");
        }
    } else if (allocation.type === 'BLOCKED') {
        if (allocation.releaseDate || allocation.releaseType || allocation.releaseDays) {
            validator.valid = false;
            validator.messages.push("Release type, release date/days has not to be set");
        }
    }
    return validator;
};

/**
 * Validate number input to be positive number above 0.
 *
 * @param stringNumber
 * @returns {boolean}
 */
export const validateMinNumber = (stringNumber) => {
    return parseInt(stringNumber) > 0;
};


/**
 * Units can only be increased in edit
 * TODO implement edit function as a request, so further editing is allowed.
 *
 * @param units
 * @param allocation
 * @returns {{valid: boolean, message: string}}
 */
export const validateUpdateUnits = (units, allocation) => {
    if (allocation.type === 'ALLOTMENT') {
        let message = "";
        let valid = units > 0 && units <= 10;

        if (!valid) {
            message = "units.set";
        }

        return {valid: valid, message: message};
    }
    return {valid: true, message: ''};
};

/**
 * Validate the update release days rules.
 *
 * @param days
 * @returns {{valid: boolean, message: string}}
 */
export const validateUpdateReleaseDays = (days) => {
    let message = "";
    let set = parseInt(days) > 0;

    if (!set) {
        message = "days.set";
    }
    return {valid: set, message: message};
};

/**
 * Validate the update release date rules.
 *
 * @param date
 * @param original
 * @returns {{valid: boolean, message: string}}
 */
export const validateUpdateReleaseDate = (date, original) => {
    let message = "";
    let valid = date !== '';
    const toDate = convertStringDateToDate(original.from);
    const newDate = convertStringDateToDate(date);
    let before = toDate.diff(newDate) > 0;
    if (!valid) {
        message = "date.set";
    } else if (!before) {
        message = "date.before";
    }
    return {valid: valid&& before, message: message};
};

/**
 * Validates if the value is present and not undefined.
 *
 * @param value
 * @param field
 * @returns {{valid: boolean, message: string}}
 */
export const required = (value, field) => {
    let message = "";
    let valid = value !== undefined && value !== "";
    if (!valid) {
        message = field + ' is required';
    }
    return {valid: valid, message: message};
};

/**
 * Checks if the email is correctly formatted.
 *
 * @param value
 * @returns {{valid: boolean, message: string}}
 */
export const validEmail = (value) => {
    let message = "";

    let valid = value !== undefined && value.indexOf('@') !== -1 && value.split('@')[1].indexOf('.') !== -1 && value.split('@')[1].split('.')[1].length >= 2;

    if (!valid) {
        message = 'Not valid email';
    }
    return {valid: valid, message: message};
};

/**
 * Validates that the password matches the password rules and that the retypedPassword equals it.
 *
 * @returns {{valid: boolean, message: string}}
 * @param password
 */
export const validatePassword = (password) => {
    let message = "";
    const _regexp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/;

    let valid = _regexp.test(password);

    if(!valid) {
        message = 'password';
    }
    return {valid: valid, message: message};
};

/**
 * Validate if to strings match.
 *
 * @param stringA
 * @param stringB
 * @returns {{valid: boolean, message: string}}
 */
export const validateStringMatch = (stringA, stringB) => {
    let message = "";

    let valid = stringA === stringB;

    if(!valid) {
        message = "mismatch";
    }
    return {valid: valid, message: message};
};



// WEBPACK FOOTER //
// ./src/services/ValidatorService.js